'use client';

import Image from '@/components/Image';
import MagicJump, { JumpTarget } from '@/components/MagicJump';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { setSession } from '@/utils/tools';
import Link from 'next/link';
import { memo, ReactNode, useEffect, useRef } from 'react';

export interface ActivityBannerData {
  image: string; // 图片或视频URL
  type?: number; // 1=视频, 2=图片
  group?: string; // 跳转的商品组ID（向后兼容）
  alt?: string; // 图片alt文本
  jump_content?: JumpTarget; // MagicJump跳转配置（新增支持）
}

interface ActivityBannerProps {
  id?: string;
  data: ActivityBannerData;
  children?: ReactNode; // 自定义内容区域
  className?: string; // 额外的样式类名
  aspectRatio?: string; // 宽高比，如 '16/9'
  height?: string; // 固定高度，如 '100vh'
  onBannerClick?: (data: ActivityBannerData) => void; // 点击回调
}

function ActivityBanner({
  id,
  data,
  children,
  className = '',
  aspectRatio,
  height,
  onBannerClick,
}: ActivityBannerProps) {
  const bannerVideoRef = useRef<HTMLVideoElement>(null);
  const { navigate } = useAppNavigation();

  useEffect(() => {
    if (bannerVideoRef.current) {
      bannerVideoRef.current.play().catch(() => {
        // 处理自动播放失败的情况
      });
    }
  }, []);

  // 向后兼容的点击处理
  const handleBannerClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (data.group) {
      setSession('zl_goodsListEnterPageScene', '活动专题页');
      navigate(`/goods-list/${data.group}`);
    }
    onBannerClick?.(data);
  };

  // MagicJump的点击处理
  const handleBeforeJump = () => {
    onBannerClick?.(data);
  };

  // 检查是否有图片数据
  if (!data?.image) {
    return null;
  }

  // 构建容器样式
  const containerStyle: React.CSSProperties = {};
  if (aspectRatio) {
    containerStyle.aspectRatio = aspectRatio;
  }
  if (height) {
    containerStyle.height = height;
  }

  const BannerContent = () => (
    <div className="banner-wrap relative block w-full h-full">
      {data.type === 2 ? (
        <Image
          src={data.image}
          alt={data.alt || 'activity banner'}
          fill
          className="block w-full h-full object-cover object-center"
        />
      ) : (
        <div className="video-slide-out w-full h-full">
          <video
            ref={bannerVideoRef}
            className="video-slide block w-full h-full object-cover object-center"
            loop
            autoPlay
            muted
            playsInline
            preload="auto"
          >
            <source src={data.image} />
          </video>
        </div>
      )}
      {/* 自定义内容区域 */}
      {children && (
        <div className="banner-content absolute inset-0 pointer-events-none">{children}</div>
      )}
    </div>
  );

  // 决定使用哪种跳转方式：MagicJump优先，然后group，最后fallback
  const shouldUseMagicJump = data.jump_content?.Jump_target;
  const shouldUseGroupLink = !shouldUseMagicJump && data.group;

  // 渲染Banner内容的函数
  const renderBannerContent = () => {
    if (shouldUseMagicJump) {
      // 使用 MagicJump 处理跳转
      return (
        <MagicJump
          item={data.jump_content}
          className="banner-wrap block w-full h-full cursor-pointer"
          onBeforeJump={handleBeforeJump}
          aria-label={data.alt || 'activity banner'}
        >
          <BannerContent />
        </MagicJump>
      );
    }

    if (shouldUseGroupLink) {
      // 向后兼容：使用group字段跳转
      return (
        <Link
          href={`/goods-list/${data.group}`}
          className="banner-wrap block w-full h-full"
          aria-label={data.alt || 'activity banner'}
          onClick={handleBannerClick}
        >
          <BannerContent />
        </Link>
      );
    }

    // 无跳转：仅响应点击事件
    return (
      <div className="banner-wrap block w-full h-full" onClick={() => onBannerClick?.(data)}>
        <BannerContent />
      </div>
    );
  };

  return (
    <div id={id} className={`activity-banner w-full ${className}`} style={containerStyle}>
      <div className="banner w-full h-full">{renderBannerContent()}</div>
    </div>
  );
}

// 使用memo防止不必要的重新渲染
const ActivityBannerMemo = memo(ActivityBanner);
export default ActivityBannerMemo;
